"use client";

import "./globals.css";
import { useMemo } from "react";
import { NextIntlClientProvider, useTranslations } from "next-intl";
import {
  globalErrorMessageBundles,
  normalizeGlobalErrorLocale,
  readNextLocaleCookie,
} from "@/i18n/globalErrorI18n";

function GlobalErrorContent({
  error,
}: Readonly<{
  error: Error & { digest?: string };
}>) {
  const t = useTranslations("SomethingWentWrongPage");

  return (
    <main className="px-6 text-center">
      <h2 className="mb-4 text-3xl">{t("title")}</h2>
      <p>{t("message")}</p>
      {error?.digest ? <p className="mt-2 text-xs">{error.digest}</p> : null}
    </main>
  );
}

export default function GlobalError({
  error,
}: Readonly<{
  error: Error & { digest?: string };
}>) {
  const resolvedLocale = useMemo(() => {
    const cookieLocale = readNextLocaleCookie();
    const browserLocale =
      typeof navigator === "undefined" ? undefined : navigator.language;

    return normalizeGlobalErrorLocale(cookieLocale || browserLocale);
  }, []);

  const resolvedMessages = globalErrorMessageBundles[resolvedLocale];

  return (
    <html lang={resolvedLocale}>
      <body className="flex min-h-screen items-center justify-center bg-white text-black">
        {/* Keep a local provider here so error strings are still translated when
            layout-level providers are unavailable during boundary rendering. */}
        <NextIntlClientProvider
          locale={resolvedLocale}
          messages={resolvedMessages}
        >
          <GlobalErrorContent error={error} />
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
